import { createClient } from '@supabase/supabase-js';

const RUNTIME_DATABASE_CONFIG_KEY = 'fms:database-config';

function readRuntimeSupabaseConfig() {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    const raw = window.localStorage.getItem(RUNTIME_DATABASE_CONFIG_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    if (parsed?.provider !== 'supabase') return null;
    if (!parsed?.supabase || typeof parsed.supabase !== 'object') return null;
    return parsed.supabase;
  } catch {
    return null;
  }
}

const runtimeSupabaseConfig = readRuntimeSupabaseConfig();
const supabaseUrl = runtimeSupabaseConfig?.url || process.env.REACT_APP_SUPABASE_URL || '';
const supabaseAnonKey = runtimeSupabaseConfig?.anonKey || process.env.REACT_APP_SUPABASE_ANON_KEY || '';
const supabaseSchema = runtimeSupabaseConfig?.schema || process.env.REACT_APP_SUPABASE_SCHEMA || 'public';
const supabaseResetPasswordRedirectUrl = process.env.REACT_APP_SUPABASE_RESET_PASSWORD_REDIRECT_URL || undefined;

export const isSupabaseConfigured = Boolean(supabaseUrl && supabaseAnonKey);

export const supabase = isSupabaseConfigured
  ? createClient(supabaseUrl, supabaseAnonKey, {
      db: { schema: supabaseSchema },
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: false,
      },
    })
  : null;

export function requireSupabaseClient() {
  if (!supabase) {
    throw new Error('Supabase is not configured. Add REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY in .env.');
  }
  return supabase;
}

export async function getSupabaseSession() {
  const client = requireSupabaseClient();
  const { data, error } = await client.auth.getSession();
  if (error) throw error;
  return data.session;
}

export function onSupabaseAuthStateChange(callback) {
  const client = requireSupabaseClient();
  return client.auth.onAuthStateChange((_event, session) => {
    callback(session);
  });
}

export async function signInWithSupabasePassword(email, password) {
  const client = requireSupabaseClient();
  const { data, error } = await client.auth.signInWithPassword({ email, password });
  if (error) throw error;
  return data.session;
}

export async function signInWithSupabaseOtp(email) {
  const client = requireSupabaseClient();
  const { data, error } = await client.auth.signInWithOtp({
    email,
    options: {
      shouldCreateUser: true,
    },
  });
  if (error) throw error;
  return data;
}

export async function signUpWithSupabasePassword(email, password) {
  const client = requireSupabaseClient();
  const { data, error } = await client.auth.signUp({ email, password });
  if (error) throw error;
  return data;
}

export async function signOutOfSupabase() {
  const client = requireSupabaseClient();
  const { error } = await client.auth.signOut();
  if (error) throw error;
}

export async function requestSupabasePasswordReset(email) {
  const client = requireSupabaseClient();
  const { data, error } = await client.auth.resetPasswordForEmail(email, {
    redirectTo: supabaseResetPasswordRedirectUrl,
  });
  if (error) throw error;
  return data;
}

export async function updateSupabaseUserMetadata(metadata) {
  const client = requireSupabaseClient();
  const { data, error } = await client.auth.updateUser({
    data: metadata,
  });
  if (error) throw error;
  return data.user;
}
